import { Link } from "@tanstack/react-router";
import {
  ChevronDown,
  ChevronRight,
  History,
  MessageSquare,
  Plus,
  Send,
  Sparkles,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Badge } from "../../components/ui/badge";
import { useTelemetry } from "../../hooks/useTelemetry";
import { useUI } from "../../stores/ui";
import { ChatMessageMarkdown } from "./ChatMessageMarkdown";
import { useSseChat } from "./useSseChat";

const SCOPE_PILLS: { id: "doc" | "col" | "all"; label: string }[] = [
  { id: "doc", label: "Documento" },
  { id: "col", label: "Colección" },
  { id: "all", label: "Todo" },
];

const SUGGESTIONS = [
  "Resume este documento",
  "¿Pasos para rollback?",
  "Diferencias entre prod y staging",
];

/**
 * B1.3 — ChatRail.
 *
 * Three states driven by `useUI.chatPanel`:
 *  - `hidden` → nothing rendered.
 *  - `rail`   → 36px vertical rail with icon + label, clicking opens.
 *  - `open`   → 320px aside with header (scope pills, history, new, collapse),
 *               thread, suggestions, input. Persists across routes.
 *
 * Source chips inside assistant text use the `{{src:sec-id}}` token; we
 * substitute them with clickable chips that call `onJumpToSection(secId)`.
 *
 * Initial draft can be primed via `useUI.scope` + URL params (set by HomeHero).
 */
export function ChatRail() {
  const state = useUI((s) => s.chatPanel);
  const setState = useUI((s) => s.setChatPanel);
  const scope = useUI((s) => s.scope);
  const setScope = useUI((s) => s.setScope);

  // Hidden state — render nothing
  if (state === "hidden") return null;

  if (state === "rail") {
    return (
      <button
        type="button"
        className="hidden w-9 flex-col items-center gap-2 border-l border-(--border-subtle) bg-(--bg-elevated) py-2 text-(--text-tertiary) hover:bg-(--bg-hover) lg:flex"
        data-testid="chat-rail"
        data-state="rail"
        onClick={() => setState("open")}
        aria-label="Abrir chat"
      >
        <MessageSquare size={15} className="text-(--accent-500)" />
        <span
          className="text-[10px] font-mono uppercase tracking-wider"
          style={{ writingMode: "vertical-rl" }}
        >
          Chat
        </span>
      </button>
    );
  }

  // state === "open"
  return (
    <aside
      data-testid="chat-rail"
      data-state="open"
      className="hidden w-80 flex-col border-l border-(--border-subtle) bg-(--bg-elevated) lg:flex"
    >
      <ChatRailHeader onClose={() => setState("rail")} scope={scope} setScope={setScope} />
      <ChatRailBody />
    </aside>
  );
}

function ChatRailHeader({
  onClose,
  scope,
  setScope,
}: {
  onClose: () => void;
  scope: "doc" | "col" | "all";
  setScope: (s: "doc" | "col" | "all") => void;
}) {
  const [historyOpen, setHistoryOpen] = useState(false);
  return (
    <header className="border-b border-(--border-subtle)">
      <div className="flex items-center justify-between px-3 py-2">
        <div className="flex items-center gap-2">
          <MessageSquare size={14} className="text-(--accent-500)" />
          <span className="text-sm font-semibold">Chat</span>
        </div>
        <div className="flex items-center gap-0.5">
          <button
            type="button"
            onClick={() => setHistoryOpen((v) => !v)}
            aria-label={historyOpen ? "Ocultar historial" : "Mostrar historial"}
            data-testid="chat-rail-history"
            className="rounded-md p-1 text-(--text-tertiary) hover:bg-(--bg-hover)"
          >
            <History size={13} />
          </button>
          <button
            type="button"
            aria-label="Nueva sesión"
            data-testid="chat-rail-new"
            className="rounded-md p-1 text-(--text-tertiary) hover:bg-(--bg-hover)"
          >
            <Plus size={13} />
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Colapsar chat"
            data-testid="chat-rail-collapse"
            className="rounded-md p-1 text-(--text-tertiary) hover:bg-(--bg-hover)"
          >
            <X size={13} />
          </button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-1 px-3 pb-2" data-testid="chat-rail-scopes">
        {SCOPE_PILLS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setScope(p.id)}
            data-testid={`chat-rail-scope-${p.id}`}
            data-active={scope === p.id ? "true" : "false"}
            className={`rounded-full px-2.5 py-1 text-[11px] font-medium transition ${
              scope === p.id
                ? "bg-(--accent-500) text-white"
                : "bg-(--bg-canvas) text-(--text-secondary) hover:bg-(--bg-hover)"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>
      {historyOpen && <ChatHistoryStub />}
    </header>
  );
}

function ChatHistoryStub() {
  const items = [
    { id: "s1", title: "Nueva sesión", when: "ahora", active: true },
    { id: "s2", title: "netbox staging", when: "hoy" },
    { id: "s3", title: "Cloudflare TLS", when: "ayer" },
    { id: "s4", title: "wrangler bindings", when: "hace 3d" },
  ];
  return (
    <ul
      data-testid="chat-rail-history-list"
      className="border-t border-(--border-subtle) bg-(--bg-canvas) px-2 py-1.5"
    >
      {items.map((it) => (
        <li
          key={it.id}
          className={`flex items-center justify-between rounded px-2 py-1 text-xs cursor-pointer ${
            it.active
              ? "bg-(--bg-active) font-medium"
              : "hover:bg-(--bg-hover) text-(--text-secondary)"
          }`}
        >
          <span className="truncate">{it.title}</span>
          <span className="ml-2 font-mono text-[10px] text-(--text-tertiary)">{it.when}</span>
        </li>
      ))}
    </ul>
  );
}

function ChatRailBody() {
  const { messages, isStreaming, send } = useSseChat();
  const scope = useUI((s) => s.scope);
  const externalDraft = useUI((s) => s.chatDraft);
  const setExternalDraft = useUI((s) => s.setChatDraft);
  const [draft, setDraft] = useState("");
  const threadRef = useRef<HTMLDivElement | null>(null);
  const track = useTelemetry();

  // Auto-scroll thread to bottom on new content
  // biome-ignore lint/correctness/useExhaustiveDependencies: messages count change drives scroll
  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [messages.length]);

  // Consume external draft (e.g. from Home ask-bar) once
  useEffect(() => {
    if (externalDraft) {
      setDraft(externalDraft);
      setExternalDraft(null);
    }
  }, [externalDraft, setExternalDraft]);

  function onSubmit() {
    const trimmed = draft.trim();
    if (!trimmed || isStreaming) return;
    void send({ question: trimmed, scope: mapScope(scope) }).catch(() => undefined);
    track("inline_chat_message", { scope, length: trimmed.length });
    setDraft("");
  }

  return (
    <>
      <div
        ref={threadRef}
        data-testid="chat-rail-thread"
        className="flex-1 overflow-y-auto px-3 py-2 space-y-3"
      >
        <ScopeBanner scope={scope} />
        {messages.length === 0 && <EmptyHints onPick={(s) => setDraft(s)} />}
        {messages.map((m) => (
          <ChatRailMessage key={m.id} message={m} />
        ))}
        {messages.length > 0 && !isStreaming && (
          <div className="flex flex-wrap gap-1 pt-2">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setDraft(s)}
                data-testid="chat-rail-suggestion"
                className="rounded-full border border-(--border-subtle) bg-(--bg-canvas) px-2.5 py-1 text-[11px] text-(--text-secondary) hover:bg-(--bg-hover)"
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>
      <form
        className="border-t border-(--border-subtle) px-2 py-2"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
        data-testid="chat-rail-form"
      >
        <div className="flex items-end gap-1.5 rounded-lg border border-(--border-subtle) bg-(--bg-canvas) p-1.5">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                onSubmit();
              }
            }}
            placeholder="Pregunta sobre este documento… ⌘↵ enviar"
            rows={2}
            data-testid="chat-rail-input"
            className="flex-1 resize-none bg-transparent text-sm text-(--text-primary) outline-none placeholder:text-(--text-tertiary)"
          />
          <button
            type="submit"
            disabled={!draft.trim() || isStreaming}
            data-testid="chat-rail-send"
            className="rounded-md bg-(--accent-500) p-1.5 text-white transition disabled:opacity-40"
            aria-label="Enviar"
          >
            <Send size={13} />
          </button>
        </div>
        <div className="mt-1 flex items-center justify-between text-[10px] font-mono text-(--text-tertiary)">
          <span>
            scope: {scope === "doc" ? "documento" : scope === "col" ? "colección" : "todo"}
          </span>
          <Link to="/chat" className="hover:underline">
            Abrir chat completo →
          </Link>
        </div>
      </form>
    </>
  );
}

function ScopeBanner({ scope }: { scope: "doc" | "col" | "all" }) {
  const label =
    scope === "doc"
      ? "Sólo este documento"
      : scope === "col"
        ? "Colección actual"
        : "Toda la documentación";
  return (
    <div
      data-testid="chat-rail-scope-banner"
      className="flex items-center gap-2 rounded-md border border-(--border-subtle) bg-(--bg-canvas) px-2.5 py-1.5 text-[11px] text-(--text-secondary)"
    >
      <Sparkles size={11} className="text-(--accent-500)" />
      <span>
        <strong>{label}</strong>
      </span>
    </div>
  );
}

function EmptyHints({ onPick }: { onPick: (s: string) => void }) {
  return (
    <div className="space-y-2 py-2">
      <p className="text-xs text-(--text-tertiary)">
        Pregunta cualquier cosa. Las respuestas vendrán con citas verificables a las secciones.
      </p>
      <div className="space-y-1">
        {SUGGESTIONS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => onPick(s)}
            data-testid="chat-rail-empty-suggestion"
            className="block w-full rounded-md border border-(--border-subtle) bg-(--bg-canvas) px-2.5 py-1.5 text-left text-xs text-(--text-secondary) hover:bg-(--bg-hover)"
          >
            "{s}"
          </button>
        ))}
      </div>
    </div>
  );
}

function ChatRailMessage({
  message,
}: {
  message: import("./useSseChat").ChatMessage;
}) {
  const isUser = message.role === "user";
  return (
    <div
      data-testid={isUser ? "chat-rail-msg-user" : "chat-rail-msg-assistant"}
      className={
        isUser
          ? "flex justify-end"
          : "rounded-md bg-(--bg-canvas) border border-(--border-subtle) px-2.5 py-2"
      }
    >
      <div className={isUser ? "max-w-[80%]" : "w-full"}>
        {!isUser && (
          <div className="mb-1 flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-(--text-tertiary)">
            <Sparkles size={9} className="text-(--accent-500)" />
            <span>DocuStudio</span>
          </div>
        )}
        {isUser ? (
          <div className="rounded-2xl bg-(--accent-500) px-3 py-1.5 text-sm text-white whitespace-pre-wrap">
            {message.content}
          </div>
        ) : message.content ? (
          <div className="text-sm text-(--text-primary)">
            <ChatMessageMarkdown text={message.content} />
            {message.streaming && (
              <span className="ml-0.5 inline-block w-1 animate-pulse bg-(--accent-500)">▍</span>
            )}
          </div>
        ) : message.streaming ? (
          <em className="text-(--text-tertiary) text-sm">pensando…</em>
        ) : null}
        {message.sources && message.sources.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {message.sources.map((src) => (
              <Link
                key={src.chunk_id}
                to="/docs/$docId"
                params={{ docId: src.doc_id }}
                className="inline-flex items-center gap-1 rounded-full bg-(--bg-elevated) px-2 py-0.5 text-[10px] no-underline hover:bg-(--bg-hover)"
                title={`Score ${src.score.toFixed(2)} · ${src.path}`}
              >
                <span>📘</span>
                <span className="max-w-[120px] truncate">{src.path.split("/").slice(-1)[0]}</span>
                <Badge variant="muted" className="text-[9px]">
                  {src.score.toFixed(2)}
                </Badge>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function mapScope(s: "doc" | "col" | "all"): string {
  if (s === "doc") return "selection";
  if (s === "col") return "project";
  return "all";
}

// Lint hygiene — unused imports tend to creep in here
void ChevronDown;
void ChevronRight;
